import React, { useState } from 'react';
import { Variable, Plus, Trash2, Edit2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { CalculatorEngine } from '../utils/calculator';

const VariablesPanel: React.FC = () => {
  const { state, dispatch } = useApp();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVarName, setNewVarName] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [editingVar, setEditingVar] = useState<string | null>(null);
  const [editVarName, setEditVarName] = useState('');
  const [editVarValue, setEditVarValue] = useState('');

  const calculator = new CalculatorEngine();
  calculator.setVariables(state.variables);

  const canEdit = !state.calculator.isLocked || state.calculator.lockedBy === state.currentUser.id;

  const addVariable = async () => {
    if (!newVarName.trim() || !newVarValue.trim()) return;
    
    if (!calculator.isValidVariableName(newVarName)) {
      alert('Variable name must start with a letter and contain only letters, numbers, and underscores');
      return;
    }

    // Check if variable name already exists
    if (state.variables.some(v => v.name === newVarName)) {
      alert('Variable name already exists');
      return;
    }

    // Validate the value
    const { isError } = calculator.evaluate(newVarValue);
    if (isError) {
      alert('Invalid value for variable');
      return;
    }

    const variable = {
      id: Date.now().toString(),
      name: newVarName,
      value: newVarValue,
      timestamp: new Date(),
      userId: state.currentUser.id,
      userName: state.currentUser.name,
      userColor: state.currentUser.color,
    };

    // Add to local state first for immediate feedback
    dispatch({ type: 'ADD_VARIABLE', payload: variable });
    setNewVarName('');
    setNewVarValue('');
    setShowAddForm(false);

    // Save to Firestore if in a session
    if (state.currentSession && state.currentUser.id) {
      try {
        const { calculatorService } = await import('../services/calculatorService');
        await calculatorService.addVariable(state.currentSession.id, variable);
        // The Firebase listener will handle updating the local state
      } catch (error) {
        console.error('Failed to save variable to Firestore:', error);
        // Variable is already in local state, so no need to add again
      }
    }
  };

  const editVariable = (variableId: string) => {
    const variable = state.variables.find(v => v.id === variableId);
    if (variable) {
      setEditingVar(variableId);
      setEditVarName(variable.name);
      setEditVarValue(variable.value);
    }
  };

  const saveEdit = async () => {
    if (!editVarName.trim() || !editVarValue.trim()) return;

    if (!calculator.isValidVariableName(editVarName)) {
      alert('Variable name must start with a letter and contain only letters, numbers, and underscores');
      return;
    }

    // Check if variable name already exists (excluding current variable)
    if (state.variables.some(v => v.name === editVarName && v.id !== editingVar)) {
      alert('Variable name already exists');
      return;
    }

    // Validate the value
    const { isError } = calculator.evaluate(editVarValue);
    if (isError) {
      alert('Invalid value for variable');
      return;
    }

    try {
      // Save to Firebase first
      if (state.currentSession) {
        const { calculatorService } = await import('../services/calculatorService');
        await calculatorService.addVariable(state.currentSession.id, {
          id: editingVar!,
          name: editVarName,
          value: editVarValue,
          timestamp: new Date(),
          userId: state.currentUser.id,
          userName: state.currentUser.name,
          userColor: state.currentUser.color,
        });
      }

      // Update local state
      dispatch({ type: 'REMOVE_VARIABLE', payload: editingVar! });
      
      const variable = {
        id: editingVar!,
        name: editVarName,
        value: editVarValue,
        timestamp: new Date(),
        userId: state.currentUser.id,
        userName: state.currentUser.name,
        userColor: state.currentUser.color,
      };

      dispatch({ type: 'ADD_VARIABLE', payload: variable });
      setEditingVar(null);
      setEditVarName('');
      setEditVarValue('');
    } catch (error) {
      console.error('Failed to save variable edit:', error);
      alert('Failed to save variable. Please try again.');
    }
  };

  const removeVariable = async (variableId: string) => {
    if (window.confirm('Are you sure you want to delete this variable?')) {
      try {
        // Remove from Firebase first
        if (state.currentSession) {
          const { calculatorService } = await import('../services/calculatorService');
          await calculatorService.removeVariable(state.currentSession.id, variableId);
        }
        
        // Update local state
        dispatch({ type: 'REMOVE_VARIABLE', payload: variableId });
      } catch (error) {
        console.error('Failed to remove variable:', error);
        alert('Failed to remove variable. Please try again.');
      }
    }
  };

  const handleUseVariable = (variableName: string) => {
    if (!canEdit) return;
    
    const currentExpression = state.calculator.currentExpression;
    const newExpression = currentExpression + variableName;
    dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: newExpression });
  };

  return (
    <div className="h-full flex flex-col p-1 sm:p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-1 sm:mb-2 flex-shrink-0">
        <div className="flex items-center space-x-1 sm:space-x-2">
          <Variable className="h-3 w-3 sm:h-4 sm:w-4 text-accent-500" />
          <h3 className="text-xs sm:text-sm font-semibold text-white">Variables</h3>
          <span className="text-xs text-dark-400">({state.variables.length})</span>
        </div>
        
        {canEdit && (
          <button
            onClick={() => setShowAddForm(true)}
            className="p-1 sm:p-1.5 bg-accent-600 hover:bg-accent-700 text-white rounded-lg transition-all duration-200 shadow-md"
            title="Add Variable"
          >
            <Plus className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Variables List - Vertical Layout */}
      <div className="flex-1 overflow-y-auto scrollbar-hide space-y-2">
        {state.variables.length === 0 ? (
          <div className="text-center text-dark-500 py-4">
            <Variable className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No variables defined</p>
            <p className="text-xs">Click + to add a variable</p>
          </div>
        ) : (
          (state.variables || []).map((variable) => (
            <div
              key={variable.id}
              className="bg-dark-700 rounded-lg p-1.5 sm:p-2 hover:bg-dark-600 transition-all duration-200 border border-dark-600 animate-slide-up"
            >
              {/* Variable Header */}
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <div
                    className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-xs font-semibold border border-dark-600"
                    style={{ backgroundColor: variable.userColor }}
                  >
                    {variable.userName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs text-dark-200 font-medium">{variable.userName}</span>
                </div>
                <span className="text-xs text-dark-500">
                  {(() => {
                    try {
                      const date = variable.timestamp instanceof Date 
                        ? variable.timestamp 
                        : (variable.timestamp as any).toDate();
                      return date.toLocaleTimeString();
                    } catch (error) {
                      return 'Invalid time';
                    }
                  })()}
                </span>
              </div>

              {/* Variable Content */}
              {editingVar === variable.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editVarName}
                    onChange={(e) => setEditVarName(e.target.value)}
                    className="w-full bg-dark-600 text-white rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent-500 border border-dark-500"
                    placeholder="Variable name"
                  />
                  <input
                    type="text"
                    value={editVarValue}
                    onChange={(e) => setEditVarValue(e.target.value)}
                    className="w-full bg-dark-600 text-white rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent-500 border border-dark-500"
                    placeholder="Variable value"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={saveEdit}
                      className="text-xs bg-success-600 hover:bg-success-700 text-white px-2 py-1 rounded transition-all duration-200 font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingVar(null);
                        setEditVarName('');
                        setEditVarValue('');
                      }}
                      className="text-xs bg-dark-600 hover:bg-dark-700 text-white px-2 py-1 rounded transition-all duration-200 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-lg font-mono font-bold text-accent-400">
                      {variable.name}
                    </span>
                    <div className="flex space-x-0.5 sm:space-x-1">
                      <button
                        onClick={() => handleUseVariable(variable.name)}
                        className="text-xs bg-accent-600 hover:bg-accent-700 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded transition-all duration-200 font-medium"
                        title="Use in calculation"
                      >
                        Use
                      </button>
                      {canEdit && (
                        <>
                          <button
                            onClick={() => editVariable(variable.id)}
                            className="p-0.5 sm:p-1 text-dark-400 hover:text-warning-400 transition-colors rounded hover:bg-dark-600"
                            title="Edit variable"
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => removeVariable(variable.id)}
                            className="p-0.5 sm:p-1 text-dark-400 hover:text-danger-400 transition-colors rounded hover:bg-dark-600"
                            title="Delete variable"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm font-mono text-dark-300 bg-dark-800 p-1.5 sm:p-2 rounded border">
                    = {variable.value}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Variable Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 w-96 shadow-2xl border border-dark-700">
            <h3 className="text-lg font-semibold text-white mb-4">Add Variable</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Variable Name
                </label>
                <input
                  type="text"
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value)}
                  placeholder="e.g., x, rate, temperature"
                  className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500 border border-dark-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Value
                </label>
                <input
                  type="text"
                  value={newVarValue}
                  onChange={(e) => setNewVarValue(e.target.value)}
                  placeholder="e.g., 25, 0.05, 100"
                  className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500 border border-dark-600"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewVarName('');
                  setNewVarValue('');
                }}
                className="px-4 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-700 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={addVariable}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-all duration-200 font-medium shadow-md"
              >
                Add Variable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariablesPanel;
